/**
 * =============================================================================
 * ROASTBREW - PUBLIC (PRE-AUTH) THEME
 * =============================================================================
 *
 * The single source of truth for every screen a signed-OUT visitor can reach:
 *
 *   UnsupportedBrowserGate  (above the router — no context of any kind)
 *   LandingPageObs          /landingpage, and embedded in AuthUI's landing view
 *   AuthUI                  /            landing / login / register / verify-sent / forgot
 *   VerifyEmailPage         /verify-email
 *   ResetPasswordPage       /reset-password
 *
 * ── WHY THIS IS SEPARATE FROM ThemeContext ───────────────────────────────────
 * ThemeContext exists to react to a runtime value: the signed-in user's tier.
 * Before sign-in there IS no tier, so there is nothing to react to. These
 * screens have exactly one look, always. A constant is the honest tool for
 * that, and it keeps the pre-auth surface fully decoupled — nothing here reads
 * TIER_COLORS, and nothing in App.jsx has to know these pages exist.
 *
 * UnsupportedBrowserGate makes the separation load-bearing rather than merely
 * tidy: it mounts ABOVE ThemeProvider and AuthContext, so it *cannot* call
 * useTheme() even if we wanted it to. A plain import is the only mechanism that
 * reaches every screen on this list.
 *
 * The values below are DERIVED from config.js TIER_COLORS (noted per token) so
 * the front door and the signed-in app read as the same product. They are
 * copied rather than imported on purpose: re-skinning the marketing surface
 * should not require touching subscriber-facing colour, and vice versa.
 *
 * ── USAGE ────────────────────────────────────────────────────────────────────
 *   import { T, PublicTheme } from './publicTheme';
 *
 *   export default function SomePublicPage() {
 *     return (
 *       <div className="rb-public">
 *         <PublicTheme />
 *         <h1 style={{ fontFamily: T.display, color: T.parchment }}>…</h1>
 *         <button className="rb-btn rb-btn--gold">Sign in</button>
 *       </div>
 *     );
 *   }
 *
 * TO RE-SKIN EVERY PRE-AUTH SCREEN: edit T. Nothing else.
 * =============================================================================
 */

import { useLayoutEffect } from 'react';

// ─────────────────────────────────────────────────────────────────────────────
// TOKENS
// ─────────────────────────────────────────────────────────────────────────────
export const T = {
  // ── COLOUR ──────────────────────────────────────────────────────────────
  void:      '#060606',   // deeper than pro_plus bg, for the hero field
  ink:       '#0D0D0D',   // TIER_COLORS.pro_plus.bg
  slate:     '#141414',   // TIER_COLORS.pro_plus.cardBg
  gold:      '#C9A962',   // TIER_COLORS.pro.accent
  gild:      '#E8D5A3',   // TIER_COLORS.pro_plus.accent
  parchment: '#F5F5F0',   // TIER_COLORS.pro.text
  muted:     '#A0A090',   // TIER_COLORS.pro.muted
  hairline:  'rgba(232,213,163,0.14)',

  // Added for the auth screens, which the landing page never needed: forms have
  // states a marketing page has none of.
  field:     'rgba(255,255,255,0.035)',  // input rest fill
  fieldEdge: 'rgba(232,213,163,0.18)',   // input rest border, one step up from hairline
  danger:    '#FF8A7A',   // lifted off #B33A3A — that red goes muddy on #0D0D0D
  success:   '#7BD98A',   // lifted off #4CAF50 for the same reason

  // The ambient depth layer, matching AppLayout's dark variant so the front
  // door and the signed-in app sit on the same field. Two corners, very low
  // alpha; the 08/05 suffixes are hex alpha (~3% and ~2%).
  ambient:
    'radial-gradient(ellipse at 0% 0%, #C9A96208 0%, transparent 50%), ' +
    'radial-gradient(ellipse at 100% 100%, #C9A96205 0%, transparent 50%)',

  // ── TYPE ────────────────────────────────────────────────────────────────
  // DM SERIF DISPLAY for display type.
  //
  // This was Bodoni Moda for one pass and it was ILLEGIBLE. Didone is the
  // textbook luxury signal — Vogue, Harper's, Dior — but that reputation was
  // earned in print at 200gsm, where a hairline is a physical ridge of ink. On a
  // screen the thin strokes fall below a pixel and the letterforms break apart.
  // Choosing the signal over legibility is a bad trade: an unreadable headline
  // communicates nothing at all, luxuriously.
  //
  // DM Serif Display keeps the editorial high-contrast feel but its thin strokes
  // are roughly twice the weight of Bodoni's, so they survive rasterisation. It
  // is also the purpose-built companion to DM Sans — same designer brief, same
  // proportions, same vertical metrics — which is why the pairing sits so
  // naturally. The secondary face is unchanged because it was already right.
  display: '"DM Serif Display", "Playfair Display", Georgia, serif',
  body:    '"DM Sans", -apple-system, BlinkMacSystemFont, sans-serif',
  label:   '"DM Sans", -apple-system, sans-serif',
};

// ─────────────────────────────────────────────────────────────────────────────
// SHARED STYLESHEET
// ─────────────────────────────────────────────────────────────────────────────
// Everything the pre-auth screens hold in common: the type scale, the button
// kit, the form kit, the focus ring. Page-specific CSS (the orrery, the section
// reveals) stays in the page that owns it.
//
// Scoped to `.rb-public`. `.rb-land` is listed alongside it on the shared
// foundation rules so LandingPageObs keeps working with its existing root class.
export const PUBLIC_CSS = `
@import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=DM+Sans:opsz,wght@9..40,300..600&display=swap');

/* Paint the ROOT, not just the component. Rubber-band overscroll at the top and
   bottom of the page reveals whatever html/body is painted with — which was the
   browser default white, flashing against a dark page every time someone
   scrolled past an end. overscroll-behavior:none also stops the bounce itself.

   THE WHITE BAR DOWN THE RIGHT EDGE is the scrollbar, not an overflow.
   color-scheme:dark asks the browser to paint its own UI dark, but it is a hint
   some engines ignore. So the scrollbar is styled explicitly too:
   scrollbar-color for Firefox, ::-webkit-scrollbar for Chrome and Safari.
   overflow-x:clip stays as a guard against any stray wide element.

   NOTE: the background rule here is belt-and-braces. App.jsx sets its own
   html/body background from the signed-in tier and its style block renders
   AFTER the routes, so at equal specificity it would win. PublicTheme also
   sets the background as an INLINE style on the elements, which beats any
   stylesheet rule regardless of order. See the component at the bottom. */
html, body { background: ${T.ink}; overscroll-behavior-y: none;
  color-scheme: dark; overflow-x: clip; max-width: 100%; }
html { scrollbar-color: rgba(232,213,163,0.22) ${T.ink}; scrollbar-width: thin; }
::-webkit-scrollbar { width: 10px; height: 10px; background: ${T.ink}; }
::-webkit-scrollbar-track { background: ${T.ink}; }
::-webkit-scrollbar-thumb { background: rgba(232,213,163,0.20);
  border: 3px solid ${T.ink}; border-radius: 0; }
::-webkit-scrollbar-thumb:hover { background: rgba(232,213,163,0.34); }
::-webkit-scrollbar-corner { background: ${T.ink}; }

/* ═══════════════════════════════════════════════════════════════════════════
   TYPE + SPACE FOUNDATION
   ───────────────────────────────────────────────────────────────────────────
   Three problems were making the landing page read as assembled rather than
   designed, and the same three now govern every pre-auth screen:

   1. THREE FAMILIES. Cormorant + DM Sans + Outfit. Now two, which is the
      discipline luxury pages actually run on.
   2. EIGHT AD-HOC MEASURES (19ch, 52ch, 56ch, 58ch, 64ch, 640px, 820px,
      1080px). A reader's eye cannot find a rhythm that is not there.
      Now four named measures, used everywhere.
   3. MIXED ALIGNMENT. Everything centres.

   The scale is a perfect fourth (1.333). Big intervals, few steps: a luxury
   page has a very large display size, a small label, and almost nothing in
   between. Timid intermediate sizes are what make a page look like
   documentation.
   ═══════════════════════════════════════════════════════════════════════════ */
.rb-public, .rb-land {
  /* measures — the only four permitted */
  --m-tight: 20ch;     /* display headlines: 3-6 words per line */
  --m-read:  46ch;     /* body under a headline */
  --m-wide:  1120px;   /* section container */
  --m-form:  420px;    /* auth card — narrower than --m-read on purpose: a
                          sign-in form is scanned, not read */

  /* type scale, perfect fourth */
  /* 8.8vw, not 11vw. The headline is ALL CAPITALS, and capitals in a
     Didone-ish serif average ~0.68em of advance against ~0.5em for mixed
     case — roughly 35% wider. Solving for the longest line gives a ceiling of
     9.45vw; 8.8 leaves headroom for font fallback before the webfont lands.
     13svh added because the 8.8vw ceiling solves the WIDTH of the longest line
     and says nothing about height — a hero has to fit the viewport it is in,
     and a viewport has two dimensions. */
  --t-display: min(8.8vw, 13svh, 9.5rem);
  --t-h2:      clamp(2.1rem, 5.2vw, 4rem);
  --t-h3:      clamp(1.75rem, 4.2vw, 2.5rem);   /* auth headings sit here */
  --t-lead:    clamp(1.0625rem, 1.5vw, 1.25rem);
  --t-body:    1rem;
  --t-small:   0.875rem;
  --t-label:   0.6875rem;

  /* vertical rhythm — one scale, used everywhere */
  --s-1: 8px;  --s-2: 16px; --s-3: 24px; --s-4: 40px;
  --s-5: 64px; --s-6: 96px; --s-7: clamp(96px, 14vh, 160px);

  /* DM Sans still carries an optical-size axis and benefits from this. */
  font-optical-sizing: auto;
  -webkit-font-smoothing: antialiased;
  text-rendering: optimizeLegibility;
}

/* overflow-x CLIP, not hidden. "hidden" quietly turns the element into a
   scroll container (overflow-y computes to auto), which made .rb-public a third
   scroller nested inside <body> — and the landing page's sticky nav stuck to
   that box, which never scrolls, so the nav scrolled away with the page. clip
   trims sideways overflow the same way without creating a scroller. "hidden"
   stays first as the fallback for engines without clip (Safari < 16). */
.rb-public{background:${T.ink};color:${T.parchment};font-family:${T.body};
  min-height:100vh;overflow-x:hidden;overflow-x:clip;max-width:100%}
.rb-public *{box-sizing:border-box}
.rb-public :focus-visible, .rb-land :focus-visible{
  outline:2px solid ${T.gild};outline-offset:3px;border-radius:4px}

/* The ambient depth layer. Fixed, inert, behind everything. */
.rb-ambient{position:fixed;inset:0;pointer-events:none;z-index:0;
  background:${T.ambient}}

/* ═══ BUTTONS ══════════════════════════════════════════════════════════════
   Caps + wide tracking on the CTA. A luxury brand never writes "Sign up" in
   sentence case on a shopfront button.

   OPTICAL CENTRING. letter-spacing appends its space AFTER the final glyph, so
   a tracked word in a centred box sits half that space left of true centre. In
   a FLEX container text-indent does not apply — children are flex items, not
   inline content — so the correction is asymmetric padding: padding-left must
   exceed padding-right by exactly one tracking unit. Every tracked-caps element
   carries this correction. */
.rb-btn{display:inline-flex;align-items:center;justify-content:center;min-height:56px;
  padding-block:0;padding-right:var(--s-4);padding-left:calc(var(--s-4) + .24em);
  border-radius:2px;font-family:${T.label};
  font-size:var(--t-label);font-weight:500;
  letter-spacing:.24em;text-transform:uppercase;
  cursor:pointer;text-decoration:none;border:.5px solid transparent;
  transition:transform .3s cubic-bezier(.2,.7,.3,1),box-shadow .35s,background .3s,border-color .3s,opacity .3s}
.rb-btn--gold{background:${T.gold};color:${T.ink};box-shadow:0 8px 34px rgba(201,169,98,0.24)}
.rb-btn--ghost{background:transparent;color:${T.parchment};border-color:${T.hairline}}
.rb-btn--block{width:100%}
/* Auth forms submit and wait; the landing page never did. */
.rb-btn:disabled{opacity:.5;cursor:not-allowed;transform:none;box-shadow:none}
.rb-btn[data-busy="true"]{cursor:wait}
@media (hover:hover){
  .rb-btn--gold:hover:not(:disabled){background:${T.gild};transform:translateY(-2px);
    box-shadow:0 12px 44px rgba(201,169,98,0.32)}
  .rb-btn--ghost:hover:not(:disabled){border-color:${T.gold};background:rgba(201,169,98,0.07)}}

/* ═══ FORM KIT ═════════════════════════════════════════════════════════════
   New here — the landing page has no inputs. Same vocabulary as the buttons:
   tracked caps for the label, 2px radius, gold on focus. */
.rb-field{display:block;margin-bottom:var(--s-3);text-align:left}
.rb-label{display:block;font-family:${T.label};font-size:var(--t-label);font-weight:500;
  letter-spacing:.18em;text-indent:.18em;text-transform:uppercase;
  color:${T.muted};margin-bottom:var(--s-1)}
.rb-input{width:100%;padding:15px 16px;font-family:${T.body};font-size:16px;
  /* 16px is not a style choice: iOS Safari zooms the viewport on focus for
     anything smaller, and never zooms back out. */
  color:${T.parchment};background:${T.field};
  border:.5px solid ${T.fieldEdge};border-radius:2px;outline:none;
  transition:border-color .3s,background .3s}
.rb-input::placeholder{color:${T.muted};opacity:.55}
.rb-input:focus{border-color:${T.gold};background:rgba(255,255,255,0.055)}
/* Chrome paints autofilled fields white and ignores background-color. The only
   reliable override is an enormous inset shadow plus -webkit-text-fill-color. */
.rb-input:-webkit-autofill,
.rb-input:-webkit-autofill:hover,
.rb-input:-webkit-autofill:focus{
  -webkit-text-fill-color:${T.parchment};
  -webkit-box-shadow:0 0 0 1000px ${T.slate} inset;
  caret-color:${T.parchment};
  transition:background-color 9999s ease-out}

/* ═══ TEXT ═════════════════════════════════════════════════════════════════ */
.rb-h{font-family:${T.display};font-weight:400;color:${T.parchment};
  font-size:var(--t-h3);line-height:1.12;letter-spacing:-.01em;margin:0 0 var(--s-2)}
.rb-note{font-family:${T.body};font-size:var(--t-small);line-height:1.7;color:${T.muted};margin:0}
.rb-note--lead{font-size:var(--t-lead);line-height:1.75}
.rb-alert{padding:13px 15px;border-radius:2px;font-family:${T.body};
  font-size:var(--t-small);line-height:1.5;text-align:left}
.rb-alert--error{color:${T.danger};background:rgba(255,138,122,0.07);
  border:.5px solid rgba(255,138,122,0.22)}

/* Inline text link, e.g. "No account? Create one" */
.rb-link{background:none;border:none;padding:0;font:inherit;cursor:pointer;
  color:${T.gild};text-decoration:underline;text-underline-offset:3px;
  text-decoration-thickness:.5px;transition:color .2s}
.rb-link--quiet{color:${T.muted}}
@media (hover:hover){.rb-link:hover{color:${T.gold}}}

/* ═══ MOTION ═══════════════════════════════════════════════════════════════ */
@keyframes rbFadeInUp{from{opacity:0;transform:translateY(18px)}to{opacity:1;transform:none}}
@keyframes rbFadeIn{from{opacity:0}to{opacity:1}}
@keyframes rbSpin{to{transform:rotate(360deg)}}
.rb-enter{animation:rbFadeInUp .6s cubic-bezier(.2,.7,.3,1)}
.rb-spinner{border-radius:50%;border:2px solid ${T.hairline};
  border-top-color:${T.gold};animation:rbSpin 1s linear infinite}

@media (prefers-reduced-motion:reduce){
  .rb-public *,.rb-public *::before,.rb-public *::after,
  .rb-land *,.rb-land *::before,.rb-land *::after{
    animation-duration:.01ms !important;animation-iteration-count:1 !important;
    transition-duration:.01ms !important;scroll-behavior:auto !important}
}
`;

// ─────────────────────────────────────────────────────────────────────────────
// <PublicTheme />
// ─────────────────────────────────────────────────────────────────────────────
//
// REFERENCE COUNTED, because these screens NEST. AuthUI's `landing` view
// renders LandingPageObs inside itself, so both components are mounted at once
// and both render a <PublicTheme/>.
//
// A naive save-and-restore breaks on exactly that case. React runs child
// effects before parent effects, so LandingPageObs would save the original
// background and set ink; AuthUI would then save *ink* and set ink. Click
// "Sign in", the landing child unmounts and restores what it saved — the
// original light value — while the parent stays mounted and never re-runs.
// Result: a light body behind a dark form.
//
// The counter fixes that: the first mount takes the snapshot, the last unmount
// restores it. Nesting is then free and no component has to know whether it is
// the outermost one.
//
// The stylesheet is injected into <head> for the same reason, and because
// @import is only valid at the top of a stylesheet — a <style> rendered inside
// the body works, but one per mounted page means duplicate @import requests.

const STYLE_ID = 'rb-public-theme';

let mountCount = 0;
let savedInlineStyles = null;

function acquirePublicTheme() {
  mountCount += 1;
  if (mountCount > 1) return;   // already applied by an ancestor

  const html = document.documentElement;
  const body = document.body;

  savedInlineStyles = {
    htmlBackground: html.style.background,
    bodyBackground: body.style.background,
    colorScheme: html.style.colorScheme,
    htmlHeight: html.style.height,
    bodyHeight: body.style.height,
    bodyOverflow: body.style.overflow,
    htmlScrollbarGutter: html.style.scrollbarGutter,
  };

  // LET THE DOCUMENT SCROLL, not <body>.
  // App.jsx's global style sets `html, body { height: 100%; overflow-x: hidden }`
  // for the signed-in app. That makes <body> a fixed-height box that scrolls
  // INSIDE the window. On the public pages that caused, on phones:
  //   - iOS Safari splitting a swipe between the window and <body>, so the page
  //     "freezes" mid-scroll and won't scroll back up until the gesture resets;
  //   - the address bar never collapsing, and the keyboard pushing fixed
  //     elements (the wordmark bar) out of place around the sign-in fields;
  //   - html's scroll-padding-top doing nothing, because <html> wasn't the
  //     thing scrolling.
  // Height auto + overflow visible on <body> hands scrolling back to the
  // window — the browser's native, best-behaved scroller. <html> keeps
  // overflow-x:hidden from App.jsx, which still stops sideways scrolling.
  // Inline, for the same reason as the background: it beats App.jsx's later
  // <style> regardless of order. Restored on release, so the signed-in app is
  // exactly as it was.
  html.style.height = 'auto';
  body.style.height = 'auto';
  body.style.overflow = 'visible';
  // App.jsx reserves the scrollbar's space on <body> because body is its
  // scroller. Here the window scrolls, so reserve it on <html> instead —
  // otherwise, on desktops with classic scrollbars, the page shifts ~10px
  // sideways when switching between the tall landing page and a short form.
  html.style.scrollbarGutter = 'stable';

  // INLINE, not via the stylesheet. App.jsx's global <style> sets
  // `html, body { background: <signed-in tier bg> }` and renders after
  // <Routes>, so at equal specificity it wins the cascade. An inline style on
  // the element beats any stylesheet rule regardless of source order. This is
  // what keeps a light strip from showing through on iOS rubber-band
  // overscroll, and it means App.jsx needs no edit.
  html.style.background = T.ink;
  body.style.background = T.ink;
  html.style.colorScheme = 'dark';

  if (!document.getElementById(STYLE_ID)) {
    const el = document.createElement('style');
    el.id = STYLE_ID;
    el.textContent = PUBLIC_CSS;
    document.head.appendChild(el);
  }
}

function releasePublicTheme() {
  mountCount -= 1;
  if (mountCount > 0) return;   // a nested page is still on screen
  mountCount = 0;               // guard against an unbalanced release

  if (savedInlineStyles) {
    document.documentElement.style.background = savedInlineStyles.htmlBackground;
    document.body.style.background = savedInlineStyles.bodyBackground;
    document.documentElement.style.colorScheme = savedInlineStyles.colorScheme;
    document.documentElement.style.height = savedInlineStyles.htmlHeight;
    document.body.style.height = savedInlineStyles.bodyHeight;
    document.body.style.overflow = savedInlineStyles.bodyOverflow;
    document.documentElement.style.scrollbarGutter = savedInlineStyles.htmlScrollbarGutter;
    savedInlineStyles = null;
  }

  const el = document.getElementById(STYLE_ID);
  if (el) el.remove();
}

/**
 * Applies the pre-auth theme for as long as it is mounted. Renders nothing.
 * Safe to mount more than once (see the reference counting above).
 *
 * useLayoutEffect, not useEffect: it runs before the browser paints, so there
 * is no frame of unstyled content on first render.
 */
export function PublicTheme() {
  useLayoutEffect(() => {
    acquirePublicTheme();
    return releasePublicTheme;
  }, []);

  return null;
}

export default T;
